<template>
  <div class="b-block__edit-profile">
    <form autocomplete="off" @submit.prevent="checkIfRequiredOtp">
      <Loader v-if="isLoading"></Loader>

      <Alert
        v-if="!isLoading && errorMsgs.length"
        key="submit-errors"
        dismiss-by-native=""
        type="danger"
        :messages="errorMsgs"
        @close="removeErrors"></Alert>

      <div class="b-block__edit-profile__form-container">
        <div class="b-block__title--lg">{{ dictionary.myProfileTitle }}</div>

        <div class="b-block__edit-profile__form">
          <div class="b-block__edit-profile__form__left-col">
            <!-- row -->
            <div class="row">
              <div class="col-lg-6">
                <div class="form-floating">
                  <input
                    id="firstname"
                    v-model.trim="firstname"
                    name="firstname"
                    type="text"
                    class="form-control"
                    :placeholder="dictionary.firstnamePlaceholder"
                    :class="{ 'form-control--error': v$.firstname.$error }"
                    @blur="v$.firstname.$touch" />

                  <label for="firstname" class="form-label">
                    {{ dictionary.firstnameLabel }}
                  </label>

                  <!-- validations -->
                  <template v-if="v$.firstname.$error">
                    <div
                      v-if="v$.firstname.required.$invalid"
                      class="field-validation-error">
                      {{ dictionary.firstnameRequired }}
                    </div>
                    <div
                      v-if="
                        v$.firstname.minLength.$invalid ||
                        v$.firstname.maxLength.$invalid
                      "
                      class="field-validation-error">
                      {{ dictionary.firstnameLength }}
                    </div>
                    <div
                      v-if="v$.firstname.regex.$invalid"
                      class="field-validation-error">
                      {{ dictionary.firstnameRegex }}
                    </div>
                  </template>
                </div>
              </div>
              <div class="col-lg-6">
                <div class="form-floating">
                  <input
                    id="lastname"
                    v-model.trim="lastname"
                    name="lastname"
                    type="text"
                    class="form-control"
                    :placeholder="dictionary.lastnamePlaceholder"
                    :class="{ 'form-control--error': v$.lastname.$error }"
                    @blur="v$.lastname.$touch" />

                  <label for="lastname" class="form-label">
                    {{ dictionary.lastnameLabel }}
                  </label>

                  <!-- validations -->
                  <template v-if="v$.lastname.$error">
                    <div
                      v-if="v$.lastname.required.$invalid"
                      class="field-validation-error">
                      {{ dictionary.lastnameRequired }}
                    </div>
                    <div
                      v-if="
                        v$.lastname.minLength.$invalid ||
                        v$.lastname.maxLength.$invalid
                      "
                      class="field-validation-error">
                      {{ dictionary.lastnameLength }}
                    </div>
                    <div
                      v-if="v$.lastname.regex.$invalid"
                      class="field-validation-error">
                      {{ dictionary.lastnameRegex }}
                    </div>
                  </template>
                </div>
              </div>
            </div>

            <!-- row -->
            <div class="row">
              <div class="col-lg-6">
                <div class="form-floating">
                  <input
                    id="email"
                    v-model.trim="email"
                    name="email"
                    type="email"
                    class="form-control"
                    :placeholder="dictionary.emailPlaceholder"
                    :class="{ 'form-control--error': v$.email.$error }"
                    formnovalidate
                    @blur="v$.email.$touch" />

                  <label for="email" class="form-label">
                    {{ dictionary.emailLabel }}
                  </label>

                  <!-- validations -->
                  <template v-if="v$.email.$error">
                    <div
                      v-if="v$.email.required.$invalid"
                      class="field-validation-error">
                      {{ dictionary.emailRequired }}
                    </div>
                    <div
                      v-if="v$.email.email.$invalid"
                      class="field-validation-error">
                      {{ dictionary.emailRegex }}
                    </div>
                  </template>
                </div>
              </div>
              <!--mobilephone-->
              <div class="col-lg-6">
                <div class="b-block__flex">
                  <div class="b-block__multiselect">
                    <multiselect
                      id="areaCode"
                      v-model="areaCode"
                      name="areaCode"
                      :options="countryOptions"
                      :searchable="true"
                      :clear-on-search="false"
                      :create-option="true"
                      :add-option-on="['tab', 'enter']"
                      :can-clear="false"
                      :search-start="true"></multiselect>
                  </div>
                  <div class="form-floating">
                    <input
                      id="mobileTelephone"
                      v-model.trim="mobileTelephone"
                      name="mobileTelephone"
                      type="text"
                      class="form-control"
                      :placeholder="dictionary.mobileTelephonePlaceholder"
                      :class="{
                        'form-control--error': v$.mobileTelephone.$error,
                      }"
                      @input="validateMobileTelephone"
                      @blur="v$.mobileTelephone.$touch" />

                    <label for="mobileTelephone" class="form-label">
                      {{ dictionary.mobileTelephoneLabel }}
                    </label>

                    <!-- validations -->
                    <template v-if="v$.mobileTelephone.$error">
                      <div
                        v-if="v$.mobileTelephone.required.$invalid"
                        class="field-validation-error">
                        {{ dictionary.mobileTelephoneRequired }}
                      </div>
                      <div
                        v-if="v$.mobileTelephone.regex.$invalid"
                        class="field-validation-error">
                        {{ dictionary.mobileTelephoneRegex }}
                      </div>
                    </template>
                  </div>
                </div>
              </div>
            </div>

            <!-- row -->
            <div class="row">
              <div class="col-lg-6">
                <div class="form-floating">
                  <input
                    id="zip"
                    v-model.trim="zip"
                    name="zip"
                    type="text"
                    class="form-control"
                    :placeholder="dictionary.zipPlaceholder"
                    :class="{ 'form-control--error': v$.zip.$error }"
                    @blur="v$.zip.$touch" />

                  <label for="zip" class="form-label">
                    {{ dictionary.zipLabel }}
                  </label>

                  <!-- validations -->
                  <template v-if="v$.zip.$error">
                    <div
                      v-if="v$.zip.numeric.$invalid"
                      class="field-validation-error">
                      {{ dictionary.zipNumeric }}
                    </div>
                  </template>
                </div>
              </div>
              <div class="col-lg-6">
                <div class="form-floating">
                  <select
                    id="gender"
                    v-model.trim="gender"
                    name="gender"
                    class="form-select">
                    <option value="">{{ dictionary.genderPlaceholder }}</option>
                    <option
                      v-for="item in genders"
                      :key="item.value"
                      :value="item.value"
                      :disabled="item.disabled">
                      {{ item.text }}
                    </option>
                  </select>

                  <label for="gender" class="form-label">
                    {{ dictionary.genderLabel }}
                  </label>
                </div>
              </div>
            </div>

            <!-- row -->
            <div class="row">
              <div class="col-lg-6">
                <div class="form-floating">
                  <Datepicker
                    v-model="dateOfBirth"
                    uid="dateOfBirth"
                    auto-apply
                    name="dateOfBirth"
                    :placeholder="dictionary.dateOfBirthPlaceholder"
                    :enable-time-picker="false"
                    :locale="configuration.locale"
                    format="dd/MM/yyyy"
                    autocomplete="off"
                    model-type="yyyy-MM-dd"
                    dark
                    reverse-years
                    prevent-min-max-navigation
                    :hide-navigation="['month', 'year']"
                    :timezone="{
                      convertModel: false,
                      exactMatch: true,
                      timezone: 'Europe/Athens',
                    }"
                    input-class-name="form-control form-control-icon"
                    :year-range="[
                      new Date().getFullYear() - 80,
                      new Date().getFullYear() - 10,
                    ]"
                    :min-date="
                      new Date(
                        new Date().getFullYear() - 80,
                        new Date().getMonth(),
                        new Date().getDay()
                      )
                    "
                    :max-date="
                      new Date(
                        new Date().getFullYear() - 10,
                        new Date().getMonth(),
                        new Date().getDay()
                      )
                    "
                    :start-date="
                      new Date(
                        new Date().getFullYear() - 18,
                        new Date().getMonth(),
                        new Date().getDay()
                      )
                    " />
                  <label
                    for="dp-input-dateOfBirth"
                    class="form-label form-label-icon">
                    {{ dictionary.dateOfBirthLabel }}
                  </label>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="p-contact__form-message">
          {{ dictionary.fieldRequired }}
        </div>
      </div>

      <div class="b-block__edit-profile__footer">
        <div></div>
        <div class="b-block__edit-profile__footer__title">
          {{ dictionary.changePasswordTitle }}
        </div>

        <div class="b-block__edit-profile__footer-holder">
          <div class="b-block__edit-profile__footer-container">
            <div class="b-block__edit-profile__footer__left-col">
              <!-- row -->
              <div class="form-floating">
                <input
                  id="new-password"
                  v-model.trim="newPassword"
                  name="new-password"
                  :type="passwordInputType"
                  class="form-control"
                  :placeholder="dictionary.newPasswordPlaceholder"
                  :class="{ 'form-control--error': v$.newPassword.$error }"
                  autocomplete="new-password"
                  @blur="v$.newPassword.$touch"
                  @focus="() => (showPasswordInfo = true)" />
                <label for="new-password" class="form-label">
                  {{ dictionary.newPasswordLabel }}
                </label>
                <button
                  class="form-floating__btn-password"
                  :class="
                    showPassword ? '' : 'form-floating__btn-password--seen'
                  "
                  type="button"
                  @click="() => (showPassword = !showPassword)"></button>

                <!-- validations -->
                <template v-if="v$.newPassword.$error">
                  <div
                    v-if="v$.newPassword.requiredIfHasValue.$invalid"
                    class="field-validation-error">
                    {{ dictionary.newPasswordRequired }}
                  </div>
                  <div
                    v-if="v$.newPassword.regex.$invalid"
                    class="field-validation-error">
                    {{ dictionary.newPasswordRegex }}
                  </div>
                </template>
              </div>
            </div>
            <div class="b-block__edit-profile__footer__right-col">
              <!-- row -->
              <div class="form-floating">
                <input
                  id="new-password-confirm"
                  v-model.trim="newPasswordConfirm"
                  name="new-password-confirm"
                  type="password"
                  class="form-control"
                  :placeholder="dictionary.newPasswordConfirmPlaceholder"
                  :class="{
                    'form-control--error': v$.newPasswordConfirm.$error,
                  }"
                  autocomplete="new-password"
                  @blur="v$.newPasswordConfirm.$touch"
                  @paste="(e) => e.preventDefault()"
                  @drop="(e) => e.preventDefault()" />

                <label for="new-password-confirm" class="form-label">
                  {{ dictionary.newPasswordConfirmLabel }}
                </label>

                <!-- validations -->
                <template v-if="v$.newPasswordConfirm.$error">
                  <div
                    v-if="v$.newPasswordConfirm.sameAsNewPassword.$invalid"
                    class="field-validation-error">
                    {{ dictionary.newPasswordConfirmMatch }}
                  </div>
                </template>
              </div>
            </div>
          </div>

          <div v-if="showPasswordInfo" class="b-info-message">
            <div
              class="b-info-message__content"
              v-html="dictionary.passwordInfo"></div>
          </div>
        </div>
      </div>

      <div class="b-block__edit-profile__membership">
        <div class="b-block__edit-profile__membership__left-col">
          <div
            v-if="!configuration.hideContactByThirdPartyCheckbox"
            class="custom-chck custom-chck--additional-style">
            <div class="custom-chck">
              <input
                id="contactByThirdParty"
                v-model="contactByThirdParty"
                type="checkbox"
                name="contactByThirdParty" />
              <label class="custom-chck__span" for="contactByThirdParty">
                {{ dictionary.contactByThirdPartyLabel }}
              </label>
            </div>
          </div>

          <div class="custom-chck custom-chck--additional-style">
            <input
              id="accept-terms"
              v-model="acceptTerms"
              type="checkbox"
              name="accept-terms"
              :class="{ 'form-control--error': v$.acceptTerms.$error }"
              @click="
                (e) => {
                  v$.acceptTerms.$touch
                }
              " />

            <label for="accept-terms">
              <span class="custom-chck__span">
                <div class="custom-chck__text">
                  <em>{{ dictionary.acceptTermsTitle }}</em>
                  <div
                    v-if="dictionary.acceptTermsLinkText"
                    v-html="dictionary.acceptTermsLinkText"></div>
                </div>
              </span>
            </label>

            <!-- validations -->
            <template v-if="v$.acceptTerms.$error">
              <div
                v-if="v$.acceptTerms.required.$invalid"
                class="field-validation-error">
                {{ dictionary.acceptTermsRequired }}
              </div>
            </template>
          </div>
        </div>
        <div class="b-block__edit-profile__membership__right-col">
          <button
            type="submit"
            class="b-block__edit-profile__membership__btn-update">
            <span>{{ dictionary.buttonSubmitText }}</span>
          </button>
        </div>
      </div>
    </form>

    <ModalOverlay
      v-model="showSuccessModal"
      background-class="b-pop-up--alert-success b-pop-up__holder"
      header-background-class="b-pop-up__header">
      <template #header>
        <div class="b-pop-up__header__title">
          {{ dictionary.submitSuccessHeader }}
        </div>
      </template>
      <template #body>
        <div class="b-pop-up__alert-success__content">
          <div
            class="b-pop-up__alert-success__icon b-pop-up__alert-success__icon--success"></div>
          <div class="b-pop-up__alert-success__content__container">
            <div class="b-pop-up__alert-success__title">
              {{ dictionary.submitSuccessTitle }}
            </div>
            <div class="b-pop-up__alert-success__text">
              {{ dictionary.submitSuccessContent }}
            </div>
            <button
              type="button"
              class="b-pop-up__alert-success__cta"
              @click="() => toggleSuccessModal(false)">
              {{ dictionary.submitSuccessClose }}
            </button>
          </div>
        </div>
      </template>
    </ModalOverlay>

    <!--otp modal-->
    <ModalOverlay
      v-model="showOtpModal"
      background-class="b-pop-up--alert-success b-pop-up__holder"
      header-background-class="b-pop-up__header">
      <template #header>
        <div class="b-pop-up__header__title"></div>
      </template>
      <template #body>
        <div class="b-pop-up__alert-success__content">
          <div class="b-pop-up__alert-success__content__container">
            <!--otp-->

            <EmailForm
              v-if="showEmailOtp"
              :email="email"
              :area-code="areaCode"
              :mobile-telephone="mobileTelephone"
              :first-name="firstname"
              :last-name="lastname"
              :show-email-otp="showEmailOtp"
              :show-telephone-otp="showTelephoneOtp"
              @prev-step-completed="enablePhoneOtp"
              @send-email-otp="getEmailOtp"></EmailForm>

            <PhoneForm
              v-if="showTelephoneOtp && !nonGreekAreaCode"
              :mobile-telephone="mobileTelephone"
              :first-name="firstname"
              :last-name="lastname"
              :show-email-otp="showEmailOtp"
              :show-telephone-otp="showTelephoneOtp"
              :prev-step-completed="eligibleToShowPhone"
              @send-phone-otp="getOtp"></PhoneForm>
          </div>
        </div>
      </template>
    </ModalOverlay>
  </div>
</template>

<script>
import * as lib from './../../lib/lib'
import constants from '../../lib/constants'
import Loader from './../components/Shared/Loader.vue'
import Alert from './../components/Shared/Alert.vue'
import useVuelidate from '@vuelidate/core'
import ModalOverlay from './../components/ModalOverlay.vue'
import postRequestHandler from '../../lib/postRequestHandler'
import getErrorMessage from '../../lib/getErrorMessage'
import Datepicker from '@vuepic/vue-datepicker'
import Multiselect from '@vueform/multiselect'
import EmailForm from './../components/EditUserForm/EmailForm.vue'
import PhoneForm from './../components/EditUserForm/PhoneForm.vue'
const { dialingCodes, regex } = constants
const { telephoneGreek, telephoneInternational, password, greekLatinSpace } =
  regex

import {
  email,
  helpers,
  maxLength,
  minLength,
  numeric,
  required,
  requiredIf,
  sameAs,
} from '@vuelidate/validators'
import { nextTick } from 'vue'

export default {
  components: {
    Loader,
    ModalOverlay,
    Alert,
    Datepicker,
    Multiselect,
    EmailForm,
    PhoneForm,
  },
  setup() {
    return { v$: useVuelidate() }
  },
  data() {
    return {
      isLoading: true,
      errorMsgs: [],
      showSuccessModal: false,
      showOtpModal: false,
      showPassword: false,
      showPasswordInfo: false,
      // personal info
      firstname: '',
      lastname: '',
      mobileTelephone: '',
      email: '',
      gender: '',
      // address info
      zip: '',
      dateOfBirth: '',
      // change password
      newPassword: '',
      newPasswordConfirm: '',
      contactByThirdParty: false,
      mobilePhoneVerificationRequired: false,
      // terms
      acceptTerms: false,
      genders: [],
      areaCode: dialingCodes.greece,
      countryOptions: null,
      initEmail: '',
      initTelephone: '',
      initAreaCode: '',
      showTelephoneOtp: false,
      showEmailOtp: false,
      nonGreekAreaCode: false,
      hasOtpError: false,
      prevStepCompleted: false,
    }
  },
  validations() {
    const validateMobileTelephone = (value) => {
      const telephoneRegex =
        this.areaCode === dialingCodes.greece
          ? telephoneGreek
          : telephoneInternational

      return !helpers.req(value) || telephoneRegex.test(value)
    }

    return {
      // personal info
      firstname: {
        required,
        minLength: minLength(2),
        maxLength: maxLength(40),
        regex: helpers.regex(greekLatinSpace),
      },
      lastname: {
        required,
        minLength: minLength(2),
        maxLength: maxLength(40),
        regex: helpers.regex(greekLatinSpace),
      },
      mobileTelephone: {
        required,
        regex: validateMobileTelephone,
      },
      email: {
        required,
        email,
      },
      zip: {
        numeric,
      },
      // change password
      newPassword: {
        requiredIfHasValue: requiredIf(this.newPasswordHasValue),
        regex: helpers.regex(password),
      },
      newPasswordConfirm: {
        sameAsNewPassword: sameAs(this.newPassword),
      },
      // terms
      acceptTerms: {
        required: sameAs(true),
      },
    }
  },

  computed: {
    eligibleToShowPhone() {
      if (this.showEmailOtp) {
        return this.prevStepCompleted
      }
      return this.showTelephoneOtp
    },

    passwordInputType() {
      return this.showPassword ? 'text' : 'password'
    },
    newPasswordHasValue() {
      return typeof this.newPassword === 'string' && this.newPassword.length > 0
    },
  },

  watch: {
    mobileTelephone(newVal) {
      const areaCode = this.areaCode
      const regex = new RegExp(`^\\+?${areaCode}`)

      if (newVal.match(regex)) {
        this.mobileTelephone = newVal.replace(regex, '')
      }
    },
    areaCode(newVal) {
      if (newVal) {
        const regex = new RegExp(`^\\+?${newVal}`)

        if (this.mobileTelephone.match(regex)) {
          this.mobileTelephone = this.mobileTelephone.replace(regex, '')
        }
      }
    },
  },

  mounted() {
    lib.checkCaptcha(() => (this.isLoading = false))
    this.setUserProfileData()
    this.setPreselectedOptions()
    const { countryOptions } = this.configuration

    this.countryOptions = [...(countryOptions || [])].sort((a, b) =>
      String(a).localeCompare(String(b), undefined, {
        numeric: true,
        sensitivity: 'base',
      })
    )

    this.nonGreekAreaCode = false
    this.keepInitValuesOfFields()
  },

  methods: {
    validateMobileTelephone() {
      this.mobileTelephone = this.mobileTelephone.replace(/[^0-9]/g, '')
    },
    enablePhoneOtp(show) {
      this.prevStepCompleted = show
    },
    keepInitValuesOfFields() {
      this.initEmail = this.email
      this.initTelephone = this.mobileTelephone
      this.initAreaCode = this.areaCode
    },
    removeErrors() {
      this.errorMsgs = []
    },
    toggleSuccessModal(show) {
      this.showSuccessModal = show
    },
    toggleOtpModal(show) {
      this.showOtpModal = show

      if (!show) {
        this.showEmailOtp = false
        this.showTelephoneOtp = false
      }
    },
    setPreselectedOptions() {
      const { genders } = lib.getData('editUserProfileFormAppData')
      this.genders = genders
      // genders
      const entry = genders.find((i) => i.value === this.gender)
      if (!entry) {
        this.gender = ''
      }
    },
    setUserProfileData() {
      const { userProfileData } = lib.getData('editUserProfileFormAppData')
      // Keep the default area code when the profile does not provide one.
      Object.assign(this.$data, {
        ...userProfileData,
        areaCode: userProfileData.areaCode || this.areaCode,
      })
    },

    resetDefaultFormData() {
      document.activeElement.blur()
      this.v$.$reset()
      this.newPassword = ''
      this.newPasswordConfirm = ''
      this.acceptTerms = false
    },

    checkIfRequiredOtp() {
      if (this.initEmail !== this.email) {
        this.requestEmailOtp()
        return
      }

      if (this.nonGreekAreaCode) {
        this.showTelephoneOtp = false
        this.onSubmitForm()
        return
      }

      if (
        this.initTelephone !== this.mobileTelephone ||
        this.initAreaCode !== this.areaCode ||
        this.mobilePhoneVerificationRequired
      ) {
        this.requestPhoneOtp()
        return
      }

      if (
        this.initEmail === this.email &&
        this.initTelephone === this.mobileTelephone &&
        this.initAreaCode === this.areaCode &&
        !this.mobilePhoneVerificationRequired
      ) {
        this.onSubmitForm()
      }
    },

    async onSubmitForm() {
      this.v$.$validate()

      if (this.isLoading) {
        return false
      }

      if (this.v$.$invalid) {
        await this.$nextTick()
        lib.scrollToValidation()
        return false
      }

      this.removeErrors()
      this.isLoading = true

      const postData = {
        firstname: this.firstname,
        lastname: this.lastname,
        areaCode: this.areaCode,
        mobileTelephone: this.mobileTelephone,
        email: this.email,
        zip: this.zip,
        gender: this.gender,
        newPassword: this.newPassword,
        newPasswordConfirm: this.newPasswordConfirm,
        dateOfBirth: this.dateOfBirth === null ? '' : this.dateOfBirth,
        acceptTerms: this.acceptTerms,
        contactByThirdParty: this.contactByThirdParty,
      }

      await postRequestHandler({
        postData,
        endpoint: this.configuration.endpointSubmitData,
        successCallback: this.handleSuccess,
        errorCallback: this.handleSubmitError,
        refreshTokenHandler: this.configuration.refresh,
        captchaAction: this.configuration.captchaAction,
      })
    },

    handleSubmitError(messages) {
      this.handleError(messages)
      this.showGlobalAlert = true
    },

    handleSuccess(response) {
      this.isLoading = false
      this.errorMsgs = []
      this.nonGreekAreaCode = !!response?.data?.nonGreekAreaCode
      this.toggleSuccessModal(true)
      lib.updateUserDisplay({
        img: response.data.avatarUrl,
        firstName: response.data.firstName,
        lastName: response.data.lastName,
        fullName: response.data.fullName,
      })
      this.resetDefaultFormData()
    },

    async handleError(messages) {
      this.isLoading = false

      this.errorMsgs = getErrorMessage(
        messages,
        this.dictionary.defaultErrorMessage
      )

      await this.$nextTick()
      lib.scrollToError()
    },

    // REQUEST EMAIL OTP
    async requestEmailOtp() {
      this.isLoading = true

      const postData = {
        email: this.email,
        userSessionId: this.configuration.userSessionId,
      }

      await postRequestHandler({
        postData,
        endpoint: this.configuration.endpointEmailOtp,
        successCallback: this.handleRequestEmailSuccess,
        errorCallback: this.handleError,
        refreshTokenHandler: this.configuration.refresh,
        captchaAction: this.configuration.startEmailCaptchaAction,
      })
    },

    handleRequestEmailSuccess() {
      this.isLoading = false
      this.errorMsgs = []
      // this.initEmail = this.email
      this.showEmailOtp = true
      this.toggleOtpModal(true)
    },

    // REQUEST PHONE OTP
    async requestPhoneOtp() {
      this.isLoading = true

      const postData = {
        firstname: this.firstname,
        lastname: this.lastname,
        areaCode: this.areaCode,
        mobilephone: this.mobileTelephone,
        userSessionId: this.configuration.userSessionId,
        language: document.documentElement.lang,
      }

      await postRequestHandler({
        postData,
        endpoint: this.configuration.endpointPhoneOtp,
        successCallback: this.handleRequestPhoneSuccess,
        errorCallback: this.handleError,
        refreshTokenHandler: this.configuration.refresh,
        captchaAction: this.configuration.startPhoneCaptchaAction,
      })
    },

    handleRequestPhoneSuccess(response) {
      this.isLoading = false
      this.nonGreekAreaCode = !!response?.data?.nonGreekAreaCode
      if (this.nonGreekAreaCode) {
        this.showTelephoneOtp = false
        this.initAreaCode = this.areaCode
        this.initTelephone = this.mobileTelephone
        this.onSubmitForm()
        this.toggleOtpModal(false)
        return
      }
      // this.initAreaCode = this.areaCode
      // this.initTelephone = this.mobileTelephone
      // this.mobilePhoneVerificationRequired = false
      this.showTelephoneOtp = true
      this.toggleOtpModal(true)
    },

    async getEmailOtp() {
      this.initEmail = this.email

      await nextTick()
      this.onSubmitForm()
      this.toggleOtpModal(false)
    },

    async getOtp() {
      this.initAreaCode = this.areaCode
      this.initTelephone = this.mobileTelephone
      this.mobilePhoneVerificationRequired = false

      await nextTick()
      this.onSubmitForm()
      this.toggleOtpModal(false)
    },

    handleEmailOtpSuccess() {
      this.isLoading = false
      this.errorMsgs = []

      this.toggleOtpModal(false)
      this.onSubmitForm()
    },
  },
}
</script>
